import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { GlassModal, GlassButton } from './ui/GlassModal';
import { useTranslation } from 'react-i18next';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  details?: string[];
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'primary' | 'neutral';
  isLoading?: boolean;
  /** Disable the confirm button without showing a loading spinner (e.g. a gate is not met). */
  confirmDisabled?: boolean;
  children?: React.ReactNode;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  details,
  confirmText,
  cancelText,
  variant = "danger",
  isLoading = false,
  confirmDisabled = false,
  children,
}) => {
  const { t } = useTranslation('common');
  const resolvedConfirmText = confirmText ?? t('actions.confirm');
  const resolvedCancelText = cancelText ?? t('actions.cancel');
  return (
    <GlassModal
      isOpen={isOpen}
      onClose={isLoading ? () => {} : onClose}
      title={title}
      maxWidth="md"
      showCloseButton={!isLoading}
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 w-full">
          <GlassButton
            variant="neutral"
            onClick={onClose}
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            {resolvedCancelText}
          </GlassButton>
          <GlassButton
            variant={variant}
            onClick={onConfirm}
            className="w-full sm:w-auto"
            loading={isLoading}
            disabled={isLoading || confirmDisabled}
          >
            {resolvedConfirmText}
          </GlassButton>
        </div>
      }
    >
      <div className="flex items-start gap-4">
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border ${
          variant === 'danger' 
            ? 'border-rose-500/30 bg-rose-500/15 text-rose-300' 
            : 'border-primary-500/30 bg-primary-500/15 text-primary-300'
        }`}>
          <AlertTriangle className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="flex-1 space-y-2 text-start">
          <p className="text-white/80">
            {message}
          </p>
          {details && details.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm text-white/60">
              {details.map((detail, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/40" />
                  {detail}
                </li>
              ))}
            </ul>
          )}
          {children}
        </div>
      </div>
    </GlassModal>
  );
};

export default ConfirmationModal;
